import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import {
  IsEnum,
  IsInt,
  IsOptional,
  IsString,
  Min,
  IsDateString,
} from 'class-validator';
import { Transform, Type } from 'class-transformer';

/**
 * Enum para los tipos de tab/sección de órdenes de servicio
 */
/* eslint-disable no-unused-vars */
export enum OsAgenciaTabType {
  ORDER_SERVICES = 'ORDER_SERVICES',
  TRANSPORT = 'TRANSPORT',
  ADDITIONAL_COST = 'ADDITIONAL_COST',
}
/* eslint-enable no-unused-vars */

/**
 * DTO para filtros de búsqueda de órdenes de servicio agencia
 */
export class OsAgenciaFiltersDto {
  @ApiPropertyOptional({ description: 'ID del cliente', example: 66 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly customerId?: number;

  @ApiPropertyOptional({ description: 'ID del servicio', example: 60 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly serviceId?: number;

  @ApiPropertyOptional({ description: 'ID de la nave', example: 7592 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly vesselId?: number;

  @ApiPropertyOptional({
    description: 'ID del tipo de facturación',
    example: 2,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly typeOfInvoicingId?: number;

  @ApiPropertyOptional({
    description: 'Número de orden de servicio',
    example: 21278,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly osNumber?: number;

  @ApiPropertyOptional({
    description: 'Número de instrucción de embarque',
    example: 4752,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly shippingInstructionId?: number;

  @ApiPropertyOptional({
    description: 'Número de orden de transporte',
    example: 1001,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly transportOrderId?: number;

  @ApiPropertyOptional({ description: 'Número de liquidación', example: 422 })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly shippingLiquidationId?: number;

  @ApiPropertyOptional({
    description: 'Número de booking',
    example: 'SNG0459048',
  })
  @IsOptional()
  @IsString()
  @Transform(({ value }) => value?.trim())
  readonly bookingNumber?: string;

  @ApiPropertyOptional({
    description: 'Número de contenedor',
    example: 'FSCU5730417',
  })
  @IsOptional()
  @IsString()
  @Transform(({ value }) => value?.trim())
  readonly containerNumber?: string;

  @ApiPropertyOptional({
    description: 'Número de DUS (CustomsLoadReference)',
    example: '070ISA1237106',
  })
  @IsOptional()
  @IsString()
  @Transform(({ value }) => value?.trim())
  readonly dusNumber?: string;

  @ApiPropertyOptional({
    description: 'ID de la temporada para filtrar',
    example: 8,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly temporadaId?: number;

  @ApiPropertyOptional({
    description: 'Fecha de inicio para filtrar (YYYY-MM-DD)',
    example: '2024-11-01',
  })
  @IsOptional()
  @IsDateString()
  readonly fromDate?: string;

  @ApiPropertyOptional({
    description: 'Fecha de fin para filtrar (YYYY-MM-DD)',
    example: '2025-06-06',
  })
  @IsOptional()
  @IsDateString()
  readonly toDate?: string;
}

/**
 * DTO para query parameters de listado de órdenes de servicio agencia
 */
export class OsAgenciaQueryDto extends OsAgenciaFiltersDto {
  @ApiProperty({
    description: 'Tipo de tab/sección',
    enum: OsAgenciaTabType,
    example: OsAgenciaTabType.ORDER_SERVICES,
  })
  @IsEnum(OsAgenciaTabType)
  readonly tabType!: OsAgenciaTabType;

  @ApiPropertyOptional({
    description: 'Página (base 1)',
    example: 1,
    minimum: 1,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly page?: number = 1;

  @ApiPropertyOptional({
    description: 'Elementos por página',
    example: 10,
    minimum: 1,
    maximum: 100,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  readonly limit?: number = 10;

  @ApiPropertyOptional({
    description: 'Campo de ordenamiento',
    example: 'creationDate',
    enum: [
      'id',
      'creationDate',
      'customerName',
      'serviceName',
      'vesselName',
      'totalAmount',
    ],
  })
  @IsOptional()
  @IsString()
  readonly orderBy?: string = 'creationDate';

  @ApiPropertyOptional({
    description: 'Dirección de ordenamiento',
    example: 'DESC',
    enum: ['ASC', 'DESC'],
  })
  @IsOptional()
  @IsString()
  readonly orderDirection?: 'ASC' | 'DESC' = 'DESC';
}

/**
 * DTO para respuesta de orden de servicio agencia
 */
export class OsAgenciaItemDto {
  @ApiProperty({ description: 'ID de la orden de servicio', example: 21278 })
  readonly id!: number;

  @ApiProperty({ description: 'ID de instrucción de embarque', example: 4752 })
  readonly shippingInstructionId!: number | null;

  @ApiProperty({ description: 'ID de orden de transporte', example: null })
  readonly transportOrderId!: number | null;

  @ApiProperty({ description: 'ID de liquidación', example: 422 })
  readonly shippingLiquidationId!: number | null;

  @ApiProperty({ description: 'ID del cliente', example: 66 })
  readonly customerId!: number;

  @ApiProperty({ description: 'Nombre del cliente', example: 'ACOGRAPES' })
  readonly customerName!: string;

  @ApiProperty({
    description: 'Nombre del servicio',
    example: 'Security Fee Contenedor',
  })
  readonly serviceName!: string;

  @ApiProperty({ description: 'Nombre de la nave', example: 'CMA CGM ESTELLE' })
  readonly vesselName!: string;

  @ApiProperty({ description: 'Tipo de facturación', example: 'Traspaso' })
  readonly typeOfInvoicingName!: string;

  @ApiProperty({ description: 'Número de booking', example: 'SNG0459048' })
  readonly bookingNumber!: string | null;

  @ApiProperty({ description: 'Número de contenedor', example: 'FSCU5730417' })
  readonly containerNumber!: string | null;

  @ApiProperty({ description: 'Número de DUS', example: '070ISA1237106' })
  readonly dusNumber!: string | null;

  @ApiProperty({ description: 'Puerto de descarga', example: 'ROTTERDAM' })
  readonly portOfDischargeName!: string | null;

  @ApiProperty({ description: 'Tipo de embarque', example: 'Contenedor' })
  readonly typeOfShipmentName!: string;

  @ApiProperty({ description: 'ID del tipo de embarque', example: 1 })
  readonly typeOfShipmentId!: number;

  @ApiProperty({ description: 'Venta Neto', example: 15.0 })
  readonly totalSellingPriceAmount!: number;

  @ApiProperty({ description: 'Traspaso Bruto', example: 0.0 })
  readonly totalManagementFeeAmount!: number;

  @ApiProperty({ description: 'ETD', example: '2025-03-14T00:00:00.000Z' })
  readonly etdDate!: Date | null;

  @ApiProperty({
    description: 'Fecha de creación',
    example: '2025-06-06T08:58:01.790Z',
  })
  readonly creationDate!: Date;

  @ApiProperty({ description: 'Creado por', example: 'karina.pelayes@opb.cl' })
  readonly createdBy!: string;

  @ApiProperty({ description: 'Referencia del cliente', example: '39/2025' })
  readonly customerReference!: string | null;

  @ApiProperty({ description: 'Comentarios', example: null })
  readonly remarks!: string | null;
}

/**
 * DTO para totales globales calculados sobre TODOS los registros filtrados
 */
export class OsAgenciaTotalsDto {
  @ApiProperty({
    description: 'Suma de Venta Neto de todos los registros filtrados',
    example: 1250000,
  })
  readonly totalVentaNeto!: number;

  @ApiProperty({
    description: 'Suma de Traspaso Bruto de todos los registros filtrados',
    example: 350000,
  })
  readonly totalTraspasoBruto!: number;

  @ApiProperty({
    description: 'Total general (ventaNeto + traspasoBruto)',
    example: 1600000,
  })
  readonly totalGeneral!: number;
}

/**
 * DTO para respuesta paginada de órdenes de servicio agencia
 */
export class OsAgenciaPaginatedResponseDto {
  @ApiProperty({
    description: 'Lista de órdenes de servicio',
    type: [OsAgenciaItemDto],
  })
  readonly data!: OsAgenciaItemDto[];

  @ApiProperty({ description: 'Total de registros', example: 6556 })
  readonly total!: number;

  @ApiProperty({ description: 'Página actual', example: 1 })
  readonly page!: number;

  @ApiProperty({ description: 'Elementos por página', example: 10 })
  readonly limit!: number;

  @ApiProperty({ description: 'Total de páginas', example: 656 })
  readonly totalPages!: number;

  @ApiProperty({ description: 'Hay página anterior', example: false })
  readonly hasPreviousPage!: boolean;

  @ApiProperty({ description: 'Hay página siguiente', example: true })
  readonly hasNextPage!: boolean;

  @ApiProperty({
    description: 'Resumen del tab actual',
    example: 'Órdenes de Servicio Agencia',
  })
  readonly tabSummary!: string;

  @ApiProperty({ description: 'Filtros aplicados' })
  readonly appliedFilters!: OsAgenciaFiltersDto;

  @ApiProperty({
    description: 'Totales globales de TODOS los registros filtrados',
    type: OsAgenciaTotalsDto,
  })
  readonly totals!: OsAgenciaTotalsDto;
}

/**
 * DTO para item de Excel de Órdenes de Servicio Agencia
 */
export class OsAgenciaExcelItemDto {
  @ApiProperty({
    description: 'Número de Orden de Servicio',
    example: 8739,
  })
  readonly osNumber!: number;

  @ApiProperty({
    description: 'Número de Instrucción de Embarque (I/E)',
    example: 3226,
    nullable: true,
  })
  readonly shippingInstructionId?: number;

  @ApiProperty({
    description: 'Número de Orden de Transporte (O/T)',
    example: null,
    nullable: true,
  })
  readonly transportOrderId?: number;

  @ApiProperty({
    description: 'Nombre del cliente',
    example: 'MONFRUT',
  })
  readonly customerName!: string;

  @ApiProperty({
    description: 'Referencia del cliente',
    example: '164',
    nullable: true,
  })
  readonly customerReference?: string;

  @ApiProperty({
    description: 'Número de viaje',
    example: '54',
  })
  readonly voyageNumber!: string;

  @ApiProperty({
    description: 'Puerto de carga',
    example: 'SAN ANTONIO',
  })
  readonly portOfLoading!: string;

  @ApiProperty({
    description: 'Terminal de carga',
    example: 'STI',
    nullable: true,
  })
  readonly terminal?: string;

  @ApiProperty({
    description: 'Fecha de salida estimada',
    example: '2024-08-14T00:00:00.000Z',
  })
  readonly etdDate!: Date;

  @ApiProperty({
    description: 'Puerto de descarga',
    example: 'NHAVA-SHEVA',
  })
  readonly portOfDischarge!: string;

  @ApiProperty({
    description: 'País de descarga',
    example: 'INDIA',
  })
  readonly countryOfDischarge!: string;

  @ApiProperty({
    description: 'Tipo de embarque',
    example: 'Contenedor',
  })
  readonly typeOfShipment!: string;

  @ApiProperty({
    description: 'Indicador de conveniencia de embarque',
    example: 'No',
    nullable: true,
  })
  readonly shipConvenience?: string;

  @ApiProperty({
    description: 'Nombre del servicio',
    example: 'Bl Fee',
  })
  readonly serviceName!: string;

  @ApiProperty({
    description: 'Tipo de facturación',
    example: 'Traspaso',
  })
  readonly typeOfInvoicing!: string;

  @ApiProperty({
    description: 'Venta neta (calculada según tipo de embarque)',
    example: 15.0,
  })
  readonly netSale!: number;

  @ApiProperty({
    description: 'Traspaso bruto',
    example: 15.0,
  })
  readonly grossTransfer!: number;

  @ApiProperty({
    description: 'Nombre del proveedor',
    example: 'A.J. BROOM Y CIA S.A.C.',
    nullable: true,
  })
  readonly providerName?: string;

  @ApiProperty({
    description: 'RUT del proveedor',
    example: null,
    nullable: true,
  })
  readonly providerRut?: string;

  @ApiProperty({
    description: 'Número de factura del proveedor',
    example: '109261',
    nullable: true,
  })
  readonly providerInvoice?: string;

  @ApiProperty({
    description: 'Cantidad de contenedores o pallets',
    example: 1.0,
  })
  readonly containers!: number;

  @ApiProperty({
    description: 'Comentarios del cliente',
    example: 'FACTURA 109261\n\nBL. SCL400294900 \nBL FEE / TELEX RELEASE',
    nullable: true,
  })
  readonly customerComments?: string;

  @ApiProperty({
    description: 'Fecha de liquidación',
    example: '2024-11-11T11:44:18.113Z',
    nullable: true,
  })
  readonly liquidationDate?: Date;

  @ApiProperty({
    description: 'ID de liquidación',
    example: 264,
    nullable: true,
  })
  readonly liquidationId?: number;
}

/**
 * DTO para respuesta del Excel de Órdenes de Servicio Agencia
 */
export class OsAgenciaExcelResponseDto {
  @ApiProperty({
    description: 'Total de registros',
    example: 4154,
  })
  readonly total!: number;

  @ApiProperty({
    description: 'Tipo de tab seleccionado',
    enum: OsAgenciaTabType,
    example: OsAgenciaTabType.ORDER_SERVICES,
  })
  readonly tabType!: OsAgenciaTabType;

  @ApiProperty({
    description: 'Lista de items para Excel',
    type: [OsAgenciaExcelItemDto],
  })
  readonly data!: OsAgenciaExcelItemDto[];

  @ApiProperty({
    description: 'Fecha de generación del reporte',
    example: '2024-08-25T01:36:28.304Z',
  })
  readonly generatedAt!: Date;
}

// DTO de servicios individuales removido - no es necesario

/**
 * DTO para datos del proveedor en detalle de OS
 */
export class OsAgenciaProviderDto {
  @ApiProperty({
    description: 'Nombre del proveedor',
    example: 'A.J. BROOM Y CIA S.A.C.',
  })
  readonly name?: string;

  @ApiProperty({ description: 'RUT del proveedor', example: '91.256.000-7' })
  readonly rut?: string;

  @ApiProperty({ description: 'Número de factura', example: '572485' })
  readonly invoiceNumber?: string;

  @ApiProperty({ description: 'Tipo de documento', example: 'B/L' })
  readonly documentType?: string;

  @ApiProperty({ description: 'Número de documento', example: 'SCL500515600' })
  readonly documentNumber?: string;

  @ApiProperty({
    description: 'Razón social completa',
    example: 'A.J. BROOM Y CIA S.A.C.',
  })
  readonly fullName?: string;

  @ApiProperty({
    description: 'Datos bancarios del proveedor',
    example: 'BANCO SANTANDER CTA CTE 64492195',
  })
  readonly bankDetails?: string;
}

/**
 * DTO para el detalle completo de una orden de servicio agencia
 * Estructura basada en el PDF de detalle de OS
 */
export class OsAgenciaDetailDto {
  // === CABECERA ===
  @ApiProperty({ description: 'Número de Orden de Servicio', example: 23613 })
  readonly nroOs!: number;

  @ApiProperty({
    description: 'Número de Instrucción de Embarque',
    example: 6238,
    nullable: true,
  })
  readonly nroIE?: number;

  @ApiProperty({
    description:
      'Número de Orden de Transporte (cuando la O/S es de transporte)',
    example: 12345,
    nullable: true,
  })
  readonly transportOrderId?: number | null;

  @ApiProperty({
    description: 'Número de reserva/booking',
    example: 'SCL500515600',
    nullable: true,
  })
  readonly reserva?: string;

  @ApiProperty({
    description: 'Referencia del cliente',
    example: '2501',
    nullable: true,
  })
  readonly refCliente?: string;

  @ApiProperty({ description: 'Nombre del cliente', example: 'ALMAHUE' })
  readonly cliente!: string;

  @ApiProperty({
    description: 'Nombre del exportador',
    example: 'PAUCO SPA',
    nullable: true,
  })
  readonly exportador?: string;

  @ApiProperty({
    description: 'Viaje (Nave + Número)',
    example: 'EVER LAMBENT 72',
    nullable: true,
  })
  readonly viaje?: string;

  @ApiProperty({ description: 'Puerto de carga (POL)', example: 'VALPARAISO' })
  readonly pol!: string;

  @ApiProperty({
    description: 'Terminal de carga',
    example: 'DPW SAI',
    nullable: true,
  })
  readonly terminal?: string;

  @ApiProperty({
    description: 'Fecha ETD',
    example: '2025-11-05T00:00:00.000Z',
  })
  readonly etd!: Date;

  @ApiProperty({
    description: 'Puerto de descarga (POD)',
    example: 'HONG KONG',
  })
  readonly pod!: string;

  @ApiProperty({
    description: 'Tipo de embarque',
    example: 'Contenedor',
    nullable: true,
  })
  readonly tipoEmbarque?: string;

  @ApiProperty({
    description: 'SHIP CONVENIENCE (S/Conv)',
    example: 'No',
    nullable: true,
  })
  readonly sConv?: string;

  @ApiProperty({
    description: 'Tratamiento frío',
    example: 'Con tratamiento',
    nullable: true,
  })
  readonly tratamientoFrio?: string;

  @ApiProperty({
    description:
      'Especie (⚠️ PENDIENTE: Consultar al cliente cómo obtener este campo)',
    example: 'Clientes',
    nullable: true,
  })
  readonly especie?: string;

  // === CONTENEDORES ASOCIADOS ===
  @ApiProperty({
    description: 'Lista de contenedores asociados a la O/S',
    example: ['FBU5413089', 'FSCU5730417'],
    type: [String],
  })
  readonly contenedores!: string[];

  // === SERVICIO ===
  @ApiProperty({
    description: 'Nombre del servicio',
    example: 'Correccion de BL',
  })
  readonly servicio!: string;

  @ApiProperty({
    description: 'Pago por cuenta de',
    example: 'Cliente',
    nullable: true,
  })
  readonly pagoPorCuentaDe?: string;

  @ApiProperty({ description: 'Tipo de facturación', example: 'Traspaso' })
  readonly tipoFacturacion!: string;

  @ApiProperty({ description: 'Cantidad', example: 1.0 })
  readonly cantidad!: number;

  @ApiProperty({
    description: 'Precio unitario',
    example: 78540.0,
    nullable: true,
  })
  readonly precioUnitario?: number;

  @ApiProperty({ description: 'Precio neto', example: 78540.0 })
  readonly precioNeto!: number;

  @ApiProperty({ description: 'IVA del precio', example: 0.0, nullable: true })
  readonly ivaPrecio?: number;

  @ApiProperty({
    description: 'Precio total a pagar',
    example: 78540.0,
    nullable: true,
  })
  readonly precioAPagar?: number;

  @ApiProperty({
    description: 'Código de moneda',
    example: 'CLP',
    nullable: true,
  })
  readonly moneda?: string;

  @ApiProperty({
    description: 'Gestión neto (Management Fee)',
    example: 8000.0,
    nullable: true,
  })
  readonly gestionNeto?: number;

  // === REFERENCIAS (Proveedor) ===
  @ApiProperty({
    description: 'Datos del proveedor',
    type: OsAgenciaProviderDto,
    nullable: true,
  })
  readonly proveedor?: OsAgenciaProviderDto;

  // === COMENTARIOS ===
  @ApiProperty({
    description: 'Comentarios del cliente',
    example:
      'Aviso: Por su instrucción, se procedio al pago de este Servicio...',
    nullable: true,
  })
  readonly comentariosCliente?: string;

  // === LIQUIDACIÓN ===
  @ApiProperty({
    description: 'Número de liquidación asociada',
    example: 509,
    nullable: true,
  })
  readonly nroLiquidacion?: number;
}
